import { useCallback, useReducer } from "react";

import { type SearchRequest } from "../types";
import { useDebounce } from "./useDebounce";

const initialState: State = {
  query: undefined,
  columns: undefined,
  filters: undefined,
  order: undefined,
  direction: undefined,
};

type State = SearchRequest;

type Action =
  | {
      type: "set_query";
      payload: string;
    }
  | {
      type: "set_columns";
      payload: string[] | undefined;
    }
  | {
      type: "add_column";
      payload: string;
    }
  | {
      type: "set_filters";
      payload: State["filters"] | undefined;
    }
  | {
      type: "add_filter";
      payload: {
        name: string;
        payload: (string | number | null) | (string | number)[];
      };
    }
  | {
      type: "set_direction";
      payload: { direction: "asc" | "desc"; order: string };
    }
  | {
      type: "clear";
      payload?: never;
    };

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "set_query":
      return { ...state, query: action.payload };
    case "set_columns":
      return { ...state, columns: action.payload };
    case "add_column":
      return {
        ...state,
        columns: state.columns
          ? [...new Set([...state.columns, action.payload])]
          : [action.payload],
      };
    case "set_filters":
      return { ...state, filters: action.payload };
    case "add_filter":
      return {
        ...state,
        filters: state.filters
          ? { ...state.filters, [action.payload.name]: action.payload.payload }
          : { [action.payload.name]: action.payload.payload },
      };
    case "set_direction":
      return {
        ...state,
        direction: action.payload.direction,
        order: action.payload.order,
      };
    case "clear":
      return { ...initialState };
    default:
      return state;
  }
};

export function useAdvanceSearch(initialValue?: Partial<State>) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...initialValue,
  });

  const debouncedSearch = useDebounce(state);
  return {
    search: state,
    debounced: debouncedSearch,
    direction: (name: string) =>
      state.order === name ? state.direction : undefined,

    setQuery: useCallback((query: string) => {
      dispatch({ type: "set_query", payload: query });
    }, []),
    setColumns: useCallback((columns: string[] | undefined) => {
      dispatch({ type: "set_columns", payload: columns });
    }, []),
    addColumn: useCallback((column: string) => {
      dispatch({ type: "add_column", payload: column });
    }, []),
    setFilters: useCallback((filters: State["filters"] | undefined) => {
      dispatch({ type: "set_filters", payload: filters });
    }, []),
    addFilter: useCallback(
      (
        name: string,
        payload: (string | number | null) | (string | number)[],
      ) => {
        dispatch({ type: "add_filter", payload: { name, payload } });
      },
      [],
    ),
    setDirection: useCallback((order: string, direction: "asc" | "desc") => {
      dispatch({ type: "set_direction", payload: { order, direction } });
    }, []),
    clear: useCallback(() => {
      dispatch({ type: "clear" });
    }, []),
  };
}
